/**
 * Sticky Banner Primitive
 *
 * Reusable wrapper component for sticky-positioned banners with optional animations.
 * Handles layout concerns like positioning, z-index, and entrance animations.
 */

import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

export type AnimationType = 'slideDown' | 'fadeIn' | 'none';

interface StickyBannerProps {
  /**
   * Content to display in the banner
   */
  children: React.ReactNode;

  /**
   * Whether the banner is visible
   */
  isVisible: boolean;

  /**
   * Top offset for sticky positioning (in pixels or CSS value)
   * @default 0
   */
  top?: number | string;

  /**
   * Z-index for layering
   * @default 1200
   */
  zIndex?: number;

  /**
   * Animation type when banner appears
   * @default 'slideDown'
   */
  animationType?: AnimationType;

  /**
   * Animation duration in seconds
   * @default 0.3
   */
  animationDuration?: number;

  /**
   * Additional sx props to merge with banner styles
   */
  sx?: SxProps<Theme>;

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Get animation keyframes and properties based on animation type
 */
const getAnimationStyles = (
  type: AnimationType,
  duration: number,
): {
  animation?: string;
  keyframes?: Record<string, unknown>;
} => {
  switch (type) {
    case 'slideDown':
      return {
        animation: `slideDown ${duration}s ease-out`,
        keyframes: {
          '@keyframes slideDown': {
            from: {
              transform: 'translateY(-100%)',
              opacity: 0,
            },
            to: {
              transform: 'translateY(0)',
              opacity: 1,
            },
          },
        },
      };

    case 'fadeIn':
      return {
        animation: `fadeIn ${duration}s ease-out`,
        keyframes: {
          '@keyframes fadeIn': {
            from: {
              opacity: 0,
            },
            to: {
              opacity: 1,
            },
          },
        },
      };

    case 'none':
    default:
      return {};
  }
};

/**
 * StickyBanner - A primitive component for creating sticky banners with animations
 *
 * @example
 * ```tsx
 * <StickyBanner isVisible={isApprovalActive} top={0} zIndex={1200}>
 *   <Paper>Banner content here</Paper>
 * </StickyBanner>
 * ```
 */
export const StickyBanner: React.FC<StickyBannerProps> = ({
  children,
  isVisible,
  top = 0,
  zIndex = 1200,
  animationType = 'slideDown',
  animationDuration = 0.3,
  sx = {},
  className,
}) => {
  if (!isVisible) return null;

  const animationStyles = getAnimationStyles(animationType, animationDuration);

  return (
    <Box
      className={className}
      sx={{
        position: 'sticky',
        top,
        zIndex,
        ...animationStyles,
        ...animationStyles.keyframes,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};
